import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import DeliverySchedule from '../components/subscription/DeliverySchedule';

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 5,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
  exit: {
    opacity: 0,
    y: -5,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
};

// Get API URL from environment variable or default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Create axios instance with auth token
const createAuthAxios = () => {
  const token = localStorage.getItem('token');
  const instance = axios.create({
    baseURL: API_URL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  if (token) {
    instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  return instance;
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const {
    currentUser,
    logout,
    getUserAddresses,
    updateProfile,
    addAddress,
    updateAddress,
    deleteAddress,
  } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [addresses, setAddresses] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [deliveries, setDeliveries] = useState({});
  const [deliveriesLoading, setDeliveriesLoading] = useState({});
  const [deliveriesError, setDeliveriesError] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addressError, setAddressError] = useState('');
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pausingSubscriptionId, setPausingSubscriptionId] = useState(null);
  const [resumeDate, setResumeDate] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressFormData, setAddressFormData] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    is_default: false,
    phone: '',
  });
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  // Fetch user addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const addressData = await getUserAddresses();
        setAddresses(addressData);
      } catch (err) {
        console.error('Error fetching addresses:', err);
        setError('Failed to load addresses');
      }
    };

    if (activeTab === 'addresses') {
      fetchAddresses();
    }
  }, [activeTab, getUserAddresses]);

  // Fetch user subscriptions and their deliveries
  useEffect(() => {
    const fetchSubscriptions = async () => {
      setLoading(true);
      setError('');
      try {
        // Create a fresh authenticated axios instance for this request
        const authAxios = createAuthAxios();
        console.log(
          'Fetching subscriptions with auth headers:',
          authAxios.defaults.headers.common['Authorization']
            ? 'Token present'
            : 'No token'
        );

        const res = await authAxios.get('/subscriptions/me');

        // Sort subscriptions by start_date in descending order (newest first)
        const sortedSubscriptions = [...res.data].sort(
          (a, b) => new Date(b.start_date) - new Date(a.start_date)
        );

        setSubscriptions(sortedSubscriptions);
        console.log('Subscriptions loaded:', sortedSubscriptions);

        // Fetch deliveries for each subscription
        sortedSubscriptions.forEach((subscription) => {
          fetchDeliveries(subscription.id);
        });
      } catch (err) {
        console.error('Error fetching subscriptions:', err);
        setError(
          `Failed to load subscriptions: ${
            err.response?.data?.detail || err.message
          }`
        );
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    }
  }, [activeTab]);

  // Fetch deliveries for a specific subscription
  const fetchDeliveries = async (subscriptionId) => {
    setDeliveriesLoading((prev) => ({ ...prev, [subscriptionId]: true }));
    setDeliveriesError((prev) => ({ ...prev, [subscriptionId]: null }));

    try {
      // Create a fresh authenticated axios instance for this request
      const authAxios = createAuthAxios();
      const res = await authAxios.get(
        `/subscriptions/me/${subscriptionId}/deliveries`
      );
      setDeliveries((prev) => ({ ...prev, [subscriptionId]: res.data }));
    } catch (err) {
      console.error(
        `Error fetching deliveries for subscription ${subscriptionId}:`,
        err
      );
      setDeliveriesError((prev) => ({
        ...prev,
        [subscriptionId]: `Failed to load deliveries: ${
          err.response?.data?.detail || err.message
        }`,
      }));
    } finally {
      setDeliveriesLoading((prev) => ({ ...prev, [subscriptionId]: false }));
    }
  };

  // Initialize profile data when user data is available
  useEffect(() => {
    if (currentUser) {
      setProfileData({
        name: currentUser.name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
      });
    }
  }, [currentUser]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await updateProfile(profileData);
      setEditMode(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCancelSubscription = async (subscriptionId) => {
    setLoading(true);
    setError('');
    try {
      // Create a fresh authenticated axios instance for this request
      const authAxios = createAuthAxios();
      await authAxios.put(`/subscriptions/me/${subscriptionId}/cancel`);
      // Refresh subscriptions
      const res = await authAxios.get(`/subscriptions/me`);
      setSubscriptions(res.data);
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(
        `Failed to cancel subscription: ${
          err.response?.data?.detail || err.message
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle showing the pause modal
  const handleShowPauseModal = (subscriptionId) => {
    setPausingSubscriptionId(subscriptionId);

    // Set default resume date to 2 weeks from today
    const twoWeeksFromNow = new Date();
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
    setResumeDate(twoWeeksFromNow.toISOString().slice(0, 10));

    setShowPauseModal(true);
  };

  // Handle pausing subscription
  const handlePauseSubscription = async () => {
    if (!pausingSubscriptionId || !resumeDate) return;

    setLoading(true);
    setError('');
    try {
      // Create a fresh authenticated axios instance for this request
      const authAxios = createAuthAxios();
      // TODO: Update API to accept resume_date parameter when implementing backend changes
      await authAxios.put(`/subscriptions/me/${pausingSubscriptionId}/pause`);

      // Refresh subscriptions
      const res = await authAxios.get(`/subscriptions/me`);
      setSubscriptions(res.data);

      // Close the modal
      setShowPauseModal(false);
      setPausingSubscriptionId(null);

      // Refetch deliveries for the updated subscription
      fetchDeliveries(pausingSubscriptionId);
    } catch (err) {
      console.error('Error pausing subscription:', err);
      setError(
        `Failed to pause subscription: ${
          err.response?.data?.detail || err.message
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle resuming subscription
  const handleResumeSubscription = async (subscriptionId) => {
    setLoading(true);
    setError('');
    try {
      // Create a fresh authenticated axios instance for this request
      const authAxios = createAuthAxios();
      await authAxios.put(`/subscriptions/me/${subscriptionId}/resume`);

      // Refresh subscriptions
      const res = await authAxios.get(`/subscriptions/me`);
      setSubscriptions(res.data);

      // Refetch deliveries for the updated subscription
      fetchDeliveries(subscriptionId);
    } catch (err) {
      console.error('Error resuming subscription:', err);
      setError(
        `Failed to resume subscription: ${
          err.response?.data?.detail || err.message
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Render profile information tab
  const renderProfileTab = () => (
    <div className="mt-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Personal Information</h2>
          <Button
            onClick={() => setEditMode(!editMode)}
            variant={editMode ? 'outline' : 'default'}
            size="sm"
          >
            {editMode ? 'Cancel' : 'Edit Profile'}
          </Button>
        </div>

        {editMode ? (
          <form onSubmit={handleProfileUpdate}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={profileData.name}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  required
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">
                  Phone number cannot be changed
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditMode(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Name</h3>
                <p className="mt-1">{currentUser?.name}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Email</h3>
                <p className="mt-1">{currentUser?.email || 'Not provided'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Phone</h3>
                <p className="mt-1">{currentUser?.phone}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  Account Created
                </h3>
                <p className="mt-1">
                  {formatDate(currentUser?.created_at) || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8">
        <Button variant="destructive" onClick={logout}>
          Sign Out
        </Button>
      </div>
    </div>
  );

  // Handle adding a new address
  const handleAddAddress = () => {
    setAddressFormData({
      recipient: currentUser?.name || '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      is_default: false,
      phone: currentUser?.phone || '',
    });
    setEditingAddressId(null);
    setShowAddressForm(true);
  };

  // Handle editing an address
  const handleEditAddress = (address) => {
    setAddressFormData({
      recipient: address.recipient || currentUser?.name || '',
      line1: address.line1 || '',
      line2: address.line2 || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country || '',
      is_default: address.is_default || false,
      phone: address.phone || currentUser?.phone || '',
    });
    setEditingAddressId(address.id);
    setShowAddressForm(true);
  };

  // Handle deleting an address
  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    setLoading(true);
    setError('');
    setAddressError('');
    try {
      await deleteAddress(addressId);
      // Refresh addresses
      const addressData = await getUserAddresses();
      setAddresses(addressData);
    } catch (err) {
      console.error('Error deleting address:', err);
      // Check if the error message mentions deliveries
      if (err.message && err.message.includes('deliveries')) {
        setAddressError(err.message);
        // Switch to subscriptions tab to help user update delivery addresses
        setActiveTab('subscriptions');
      } else {
        setError('Failed to delete address');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle address form input changes
  const handleAddressInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle saving an address (create or update)
  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Create a copy of the form data
      const processedAddressData = {
        ...addressFormData,
      };

      console.log('Processed address data:', processedAddressData);

      if (editingAddressId) {
        // Update existing address
        await updateAddress(editingAddressId, processedAddressData);
      } else {
        // Create new address
        await addAddress(processedAddressData);
      }

      // Refresh addresses
      const addressData = await getUserAddresses();
      setAddresses(addressData);
      setShowAddressForm(false);
    } catch (err) {
      console.error('Error saving address:', err);
      setError('Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  // Render addresses tab
  const renderAddressesTab = () => (
    <div className="mt-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Your Addresses</h2>
          {!showAddressForm && (
            <Button variant="default" size="sm" onClick={handleAddAddress}>
              Add New Address
            </Button>
          )}
        </div>

        {showAddressForm ? (
          <div className="bg-gray-50 p-4 rounded-md">
            <h3 className="text-lg font-medium mb-4">
              {editingAddressId ? 'Edit Address' : 'Add New Address'}
            </h3>
            <form onSubmit={handleSaveAddress} className="space-y-4">
              <div className="space-y-1.5">
                <label
                  htmlFor="recipient"
                  className="block text-sm font-medium text-gray-700"
                >
                  Full Name
                </label>
                <input
                  type="text"
                  id="recipient"
                  name="recipient"
                  value={addressFormData.recipient}
                  onChange={handleAddressInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="line1"
                  className="block text-sm font-medium text-gray-700"
                >
                  Address Line 1
                </label>
                <input
                  type="text"
                  id="line1"
                  name="line1"
                  value={addressFormData.line1}
                  onChange={handleAddressInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="line2"
                  className="block text-sm font-medium text-gray-700"
                >
                  Address Line 2 (Optional)
                </label>
                <input
                  type="text"
                  id="line2"
                  name="line2"
                  value={addressFormData.line2}
                  onChange={handleAddressInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label
                    htmlFor="city"
                    className="block text-sm font-medium text-gray-700"
                  >
                    City
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={addressFormData.city}
                    onChange={handleAddressInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="state"
                    className="block text-sm font-medium text-gray-700"
                  >
                    State
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={addressFormData.state}
                    onChange={handleAddressInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label
                    htmlFor="postal_code"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Postal Code
                  </label>
                  <input
                    type="text"
                    id="postal_code"
                    name="postal_code"
                    value={addressFormData.postal_code}
                    onChange={handleAddressInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="country"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Country
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={addressFormData.country}
                    onChange={handleAddressInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={addressFormData.phone}
                  onChange={handleAddressInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_default"
                  name="is_default"
                  checked={addressFormData.is_default}
                  onChange={handleAddressInputChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label
                  htmlFor="is_default"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Set as default address
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddressForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Address'}
                </Button>
              </div>
            </form>
          </div>
        ) : loading ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading addresses...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
          </div>
        ) : addresses.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">You don't have any saved addresses.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleAddAddress}
            >
              Add Your First Address
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {addresses.map((address) => (
              <div
                key={address.id}
                className="border border-gray-200 rounded-md p-4"
              >
                <div className="flex justify-between">
                  <div>
                    {address.is_default && (
                      <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mb-2">
                        Default
                      </span>
                    )}
                    <p className="font-medium">{address.recipient}</p>
                    <p className="text-sm text-gray-600">
                      {address.line1}
                      {address.line2 && `, ${address.line2}`}
                    </p>
                    <p className="text-sm text-gray-600">
                      {address.city}, {address.state} - {address.postal_code}
                    </p>
                    <p className="text-sm text-gray-600">{address.country}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Phone: {address.phone}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditAddress(address)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteAddress(address.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render subscriptions tab
  const renderSubscriptionsTab = () => (
    <div className="mt-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Your Subscriptions</h2>

        {loading ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading subscriptions...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              You don't have any active subscriptions.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/')}
            >
              Browse Flowers
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {subscriptions.map((subscription) => (
              <div
                key={subscription.id}
                className="border border-gray-200 rounded-md p-4"
              >
                <div className="flex flex-col md:flex-row justify-between">
                  <div>
                    <div className="flex items-center">
                      <h3 className="font-medium">
                        {subscription.duration} Subscription
                      </h3>
                      <span
                        className={`ml-2 px-2 py-1 text-xs rounded-full ${
                          subscription.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : subscription.status === 'paused'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {subscription.status.charAt(0).toUpperCase() +
                          subscription.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Started: {formatDate(subscription.start_date)}
                    </p>
                    {subscription.end_date && (
                      <p className="text-sm text-gray-600">
                        Ends: {formatDate(subscription.end_date)}
                      </p>
                    )}
                    <p className="text-sm text-gray-600">
                      Total Amount: ₹{subscription.total_amount.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex space-x-2 mt-4 md:mt-0">
                    {subscription.status === 'active' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleShowPauseModal(subscription.id)}
                        >
                          Pause
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() =>
                            handleCancelSubscription(subscription.id)
                          }
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    {subscription.status === 'paused' && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() =>
                          handleResumeSubscription(subscription.id)
                        }
                      >
                        Resume
                      </Button>
                    )}
                    {subscription.status === 'cancelled' && (
                      <Button variant="outline" size="sm" disabled>
                        Cancelled
                      </Button>
                    )}
                  </div>
                </div>

                {/* Delivery Schedule */}
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <DeliverySchedule
                    deliveries={deliveries[subscription.id] || []}
                    loading={deliveriesLoading[subscription.id]}
                    error={deliveriesError[subscription.id]}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pause Modal */}
        {showPauseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-xl font-semibold mb-4">Pause Subscription</h3>
              <p className="text-gray-600 mb-4">
                Your subscription will be paused immediately and will
                automatically resume on the selected date. No deliveries will be
                made during the pause period, and you will not be charged.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resume Date
                </label>
                <input
                  type="date"
                  value={resumeDate}
                  onChange={(e) => setResumeDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPauseModal(false);
                    setPausingSubscriptionId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePauseSubscription}
                  disabled={loading || !resumeDate}
                >
                  {loading ? 'Processing...' : 'Pause Subscription'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="py-[120px] px-5 max-w-[1000px] mx-auto"
    >
      <h1 className="text-3xl font-bold">My Account</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mt-4">
          {error}
        </div>
      )}

      {addressError && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mt-4">
          <p className="font-medium">Action Required:</p>
          <p>{addressError}</p>
          <p className="mt-2">
            You've been redirected to the Subscriptions tab to update delivery
            addresses.
          </p>
        </div>
      )}

      <div className="mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab('addresses')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'addresses'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Addresses
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'subscriptions'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Subscriptions
            </button>
          </nav>
        </div>

        {activeTab === 'profile' && renderProfileTab()}
        {activeTab === 'addresses' && renderAddressesTab()}
        {activeTab === 'subscriptions' && renderSubscriptionsTab()}
      </div>
    </motion.div>
  );
};

export default ProfilePage;
